function stripHtml(value = "") {
  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function estimateReadingTime(value = "") {
  const wordCount = stripHtml(value).split(" ").filter(Boolean).length;
  const minutes = Math.max(1, Math.ceil(wordCount / 200));

  return `${minutes} dk`;
}

function extractRendered(field) {
  if (typeof field === "string") {
    return field;
  }

  if (field && typeof field === "object" && "rendered" in field) {
    return field.rendered || "";
  }

  return "";
}

function extractFeaturedMedia(item) {
  const media = item?._embedded?.["wp:featuredmedia"]?.[0];

  return {
    url: media?.source_url || "",
    alt: media?.alt_text || extractRendered(item?.title) || "",
  };
}

function extractTerms(item, taxonomy) {
  const termGroups = item?._embedded?.["wp:term"] || [];
  const normalized = termGroups.flat().filter(Boolean);

  return normalized
    .filter((term) => term.taxonomy === taxonomy)
    .map((term) => term.name);
}

function extractPrimaryCategory(item) {
  return (
    extractTerms(item, "category")[0] ||
    extractTerms(item, "app_category")[0] ||
    "Genel"
  );
}

function normalizeArray(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function extractPlatforms(item) {
  const acfPlatforms = [
    ...normalizeArray(item?.acf?.platform),
    ...normalizeArray(item?.acf?.platforms),
  ];
  const taxonomyPlatforms = extractTerms(item, "platform");

  return [...new Set([...acfPlatforms, ...taxonomyPlatforms])].filter(Boolean);
}

function extractTags(item) {
  const taxonomyTags = extractTerms(item, "post_tag");
  const appTags = extractTerms(item, "app_tag");
  const acfTags = normalizeArray(item?.acf?.tags);

  return [...new Set([...taxonomyTags, ...appTags, ...acfTags])];
}

export function mapWordPressPost(item) {
  const featuredMedia = extractFeaturedMedia(item);
  const content = extractRendered(item.content);
  const excerpt = extractRendered(item.excerpt);

  return {
    id: String(item.id),
    slug: item.slug,
    title: stripHtml(extractRendered(item.title)),
    excerpt: stripHtml(excerpt) || stripHtml(content).slice(0, 180),
    content,
    cover: featuredMedia.alt || "Blog kapak gorseli",
    coverImage: featuredMedia.url,
    category: extractPrimaryCategory(item),
    tags: extractTags(item),
    readingTime: estimateReadingTime(content),
    publishedAt: item.date,
    featured: Boolean(item?.acf?.featured),
    seo: item?.yoast_head_json || null,
  };
}

export function mapWordPressApp(item) {
  const featuredMedia = extractFeaturedMedia(item);
  const content = extractRendered(item.content);
  const excerpt = extractRendered(item.excerpt);
  const platforms = extractPlatforms(item);

  return {
    id: String(item.id),
    slug: item.slug,
    name: stripHtml(extractRendered(item.title)),
    category: extractPrimaryCategory(item),
    platform: platforms.length ? platforms : ["Web"],
    status: item?.acf?.status || "Draft",
    summary: stripHtml(excerpt) || stripHtml(content).slice(0, 160),
    description: stripHtml(content) || stripHtml(excerpt),
    content,
    tags: extractTags(item),
    storeUrl: item?.acf?.store_url || "#",
    demoUrl: item?.acf?.demo_url || "#",
    featured: Boolean(item?.acf?.featured),
    cover: featuredMedia.alt || "Proje kapak gorseli",
    coverImage: featuredMedia.url,
    seo: item?.yoast_head_json || null,
  };
}

export function mapWordPressPage(item) {
  const featuredMedia = extractFeaturedMedia(item);
  const content = extractRendered(item.content);

  return {
    id: String(item.id),
    slug: item.slug,
    title: stripHtml(extractRendered(item.title)),
    excerpt: stripHtml(extractRendered(item.excerpt)),
    content,
    coverImage: featuredMedia.url,
    seo: item?.yoast_head_json || null,
  };
}

export function mapWordPressTerm(item) {
  return {
    id: item.id,
    slug: item.slug,
    name: item.name,
    count: item.count || 0,
  };
}

export function mapSiteSettings(item = {}) {
  return {
    siteProfile: item.siteProfile || null,
    homepageSections: item.homepageSections || null,
    featuredMetrics: item.featuredMetrics || [],
    expertiseAreas: item.expertiseAreas || [],
    technologyStack: item.technologyStack || [],
    careerHighlights: item.careerHighlights || [],
    skillCards: item.skillCards || [],
    contactInfo: item.contactInfo || null,
    socialLinks: item.socialLinks || [],
    header: item.header || null,
    footer: item.footer || null,
    seo: item.seo || null,
  };
}

export function mapNavigation(item = {}) {
  return {
    items: Array.isArray(item.items) ? item.items : [],
  };
}
